import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import {
  ENUM_VALUE_TYPE,
  STRING_VALUE_TYPE,
  BOOLEAN_VALUE_TYPE,
  NUMBER_VALUE_TYPE,
  CELL_LAYOUT_TYPE,
  LABELLED_LAYOUT_TYPE,
  FULL_LAYOUT_TYPE,
} from './Types';
import EnumDialogEditor from './EnumDialogEditor';
import ArrayDialogEditor from './ArrayDialogEditor';
import StringDialogEditor from './StringDialogEditor';
import BooleanDialogEditor from './BooleanDialogEditor';
import NumberDialogEditor from './NumberDialogEditor';
import FilterDialogPanel from './FilterDialogPanel';
import './FilterDialog.scss';

const FilterDialog = (props) => {
  const {
    onClose,
    filterEditor,
    value,
    open,
    ...rest
  } = props;
  const [dialogValue, setDialogValue] = useState(value);
  const validate = useCallback((values, pps) => {
    const errors = {};
    filterEditor.propertyEditors.forEach((editor) => {
      if (editor.editable === false) {
        return;
      }
      if (editor.validators) {
        editor.validators.forEach((v) => {
          const e = v(values[editor.name], values, { ...pps, editor });
          if (e) {
            errors[editor.name] = e;
          }
        });
      }
    });
    if (filterEditor.validators) {
      filterEditor.validators.forEach((v) => {
        const e = v(values, pps);
        if (e) {
          Object.assign(errors, e);
        }
      });
    }
    return errors;
  }, [filterEditor]);
  const [errors, setErrors] = useState(() => validate(dialogValue, props));
  const [tabComponents] = useState(() => filterEditor.tabs && filterEditor.tabs.map(tab => ({ ...tab, type: FilterDialogPanel })));
  const handleCancel = () => onClose();
  const handleSearch = () => onClose(dialogValue);
  const onValueChange = editor => (v) => {
    const newValues = Object.assign({}, dialogValue, { [editor.name]: v });
    // For each editor that depends on editor, update the current value
    filterEditor.propertyEditors
    .filter(e => (e.dependsOn && (e.dependsOn.indexOf(editor.name) >= 0)))
    .forEach((e) => {
      newValues[e.name] = editor.updateValue
        ? editor.updateValue(dialogValue[e.name], { ...rest, [editor.name]: v })
        : undefined;
    });
    setDialogValue(newValues);
    setErrors(validate(newValues, props));
  };
  const handleKeyEvent = (ev) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      onClose(dialogValue);
    }
  };

  const editorsByLayoutType = {};
  filterEditor.propertyEditors.forEach((editor) => {
    if (editor.editable === false) {
      return;
    }
    let { Component } = editor;
    let defaultLayoutType;
    if (!Component) {
      switch (editor.type) {
        case ENUM_VALUE_TYPE: {
          Component = EnumDialogEditor;
          defaultLayoutType = CELL_LAYOUT_TYPE;
          break;
        }
        case STRING_VALUE_TYPE: {
          Component = StringDialogEditor;
          defaultLayoutType = CELL_LAYOUT_TYPE;
          break;
        }
        case BOOLEAN_VALUE_TYPE: {
          Component = BooleanDialogEditor;
          defaultLayoutType = LABELLED_LAYOUT_TYPE;
          break;
        }
        case NUMBER_VALUE_TYPE: {
          Component = NumberDialogEditor;
          defaultLayoutType = CELL_LAYOUT_TYPE;
          break;
        }
        default: {
          Component = ArrayDialogEditor;
          defaultLayoutType = LABELLED_LAYOUT_TYPE;
        }
      }
    } else {
      defaultLayoutType = FULL_LAYOUT_TYPE;
    }
    const layoutType = editor.layout || defaultLayoutType;
    let layoutEditors = editorsByLayoutType[layoutType];
    if (!layoutEditors) {
      layoutEditors = [];
      editorsByLayoutType[layoutType] = layoutEditors;
    }
    layoutEditors.push({ editor, Component });
  });
  const TabsComponent = filterEditor.tabs && filterEditor.tabsComponent;
  const firstErrorName = Object.keys(errors).find(name => errors[name] !== undefined);
  const firstError = firstErrorName
    ? { title: filterEditor.propertyEditors.find(pe => pe.name === firstErrorName).title, error: errors[firstErrorName] }
    : undefined;

  return (
    <Dialog
      aria-labelledby="filter-dialog-title"
      className={classnames('grbrsm-filter-dialog', filterEditor.dialogClassName)}
      open={open}
      onClose={handleCancel}
      onKeyPress={handleKeyEvent}
    >
      <DialogContent className="grb-filter-dialog-content grbrsm-filter-dialog-content">
        {filterEditor.tabs ? (
          <TabsComponent
            {...rest}
            defaultTabId={filterEditor.defaultTabId || tabComponents[0].id}
            components={tabComponents}
            editorsByLayoutType={editorsByLayoutType}
            dialogValue={dialogValue}
            dialogErrors={errors}
            onValueChange={onValueChange}
          />
        ) : (
          <FilterDialogPanel
            {...rest}
            editorsByLayoutType={editorsByLayoutType}
            dialogValue={dialogValue}
            dialogErrors={errors}
            onValueChange={onValueChange}
          />
        )}
      </DialogContent>
      <DialogActions>
        <div className="grb-flex" />
        <Button key="filterCancel" onClick={handleCancel} id="filterDialogCancelButton">Cancel</Button>
        {firstError ? (
          <Tooltip title={`${firstError.title}: ${firstError.error}`}>
            <span>
              <Button
                key="filterSearch"
                onClick={handleSearch}
                disabled={!!firstError}
                id="filterDialogApplyButton"
              >
                Filter
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button key="filterSearch" onClick={handleSearch} id="filterDialogApplyButton">
            Filter
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

FilterDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  filterEditor: PropTypes.shape({
    dialogClassName: PropTypes.string,
    propertyEditors: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
    })),
    tabs: PropTypes.arrayOf(PropTypes.shape({})),
    defaultTabId: PropTypes.string,
    tabsComponent: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({}),
    ]),
    validators: PropTypes.arrayOf(PropTypes.func),
  }).isRequired,
  value: PropTypes.shape({}).isRequired,
  open: PropTypes.bool.isRequired,
};

FilterDialog.defaultProps = {
};

export default FilterDialog;
